import { Observable, catchError, map, of, shareReplay, tap } from 'rxjs'
import { Injectable } from '@angular/core'

import { CatalogResponse, DocumentEntityType, HolderType, RefreshNeededEntry, RenameDocumentFilePayload, ReviewDocumentPayload } from '@data/interfaces/documents.interface'
import { IResponse } from '@data/interfaces/iresponseArray.interface'
import { ApiClass } from '@data/class/api-class'
import { environment } from '@env/environment'

/**
 * Servicio del expediente documental del cliente (ADR-0024).
 * La subida/borrado de archivos individuales la manejan `app-input-single-file` /
 * `app-input-multi-file` contra `documents/upload` y `documents/delete`; aquí vive
 * la persistencia de la estructura (PUT) y la gestión de avales + catálogo.
 */
@Injectable({ providedIn: 'root' })
export class ApiDocumentsService extends ApiClass {
  constructor() {
    super()
    this.path = 'documents'
  }

  //* El catálogo es fijo mientras dura la sesión: se pide una vez y se reparte entre los expedientes que se montan.
  private catalog$: Observable<CatalogResponse | null> | null = null

  /** GET documents/catalog, en caché. Un resultado sin catálogo (error o vacío) no se guarda: el siguiente llamado reintenta. */
  getCatalog(): Observable<CatalogResponse | null> {
    if (!this.catalog$) {
      this.catalog$ = this.http.get<IResponse>(`${environment.apiUrl}documents/catalog`).pipe(
        map((r: any) => (r?.data ?? r) as CatalogResponse),
        catchError(() => of(null)),
        tap((res) => {
          if (!res?.catalog) this.catalog$ = null
        }),
        shareReplay(1),
      )
    }
    return this.catalog$
  }

  /** PUT documents/:id — persiste el subárbol (holder o guarantors) completo. */
  updateDocs(docsId: string, documentType: DocumentEntityType, updateDocuments: Record<string, any>): Observable<IResponse> {
    return this.http
      .put<IResponse>(`${environment.apiUrl}documents/${docsId}`, { documentType, updateDocuments })
      .pipe(catchError((e) => of({ error: true, msg: e?.error?.msg ?? 'Error al guardar', data: null })))
  }

  addGuarantor(docsId: string, documentType: DocumentEntityType, type: HolderType, name?: string): Observable<IResponse> {
    return this.http
      .post<IResponse>(`${environment.apiUrl}documents/${docsId}/guarantors`, { documentType, type, name: name || '' })
      .pipe(catchError((e) => of({ error: true, msg: e?.error?.msg ?? 'Error al agregar aval', data: null })))
  }

  removeGuarantor(docsId: string, documentType: DocumentEntityType, guarantorId: string): Observable<IResponse> {
    return this.http
      .delete<IResponse>(`${environment.apiUrl}documents/${docsId}/guarantors/${guarantorId}?documentType=${documentType}`)
      .pipe(catchError((e) => of({ error: true, msg: e?.error?.msg ?? 'Error al eliminar aval', data: null })))
  }

  /**
   * POST documents/:docsId/review — aprueba/rechaza un archivo del expediente.
   * `conflict: true` cuando el backend responde 409 (el expediente cambió:
   * índice/url desfasados) → el caller debe recargar y avisar.
   */
  reviewDocument(docsId: string, payload: ReviewDocumentPayload): Observable<IResponse & { conflict?: boolean }> {
    return this.http
      .post<IResponse>(`${environment.apiUrl}documents/${docsId}/review`, payload)
      .pipe(catchError((e) => of({ error: true, msg: e?.error?.msg ?? 'Error al revisar el documento', data: null, conflict: e?.status === 409 })))
  }

  /** PATCH documents/:docsId/file-name — renombra el nombre visible de un archivo (solo staff). */
  renameDocumentFile(docsId: string, payload: RenameDocumentFilePayload): Observable<IResponse & { conflict?: boolean }> {
    return this.http
      .patch<IResponse>(`${environment.apiUrl}documents/${docsId}/file-name`, payload)
      .pipe(catchError((e) => of({ error: true, msg: e?.error?.msg ?? 'Error al renombrar el archivo', data: null, conflict: e?.status === 409 })))
  }

  getRefreshStatus(docsId: string, documentType: DocumentEntityType): Observable<RefreshNeededEntry[]> {
    return this.http.get<any>(`${environment.apiUrl}documents/${docsId}/refresh-status?documentType=${documentType}`).pipe(
      map((r: any) => (r?.data?.toRefresh ?? r?.toRefresh ?? []) as RefreshNeededEntry[]),
      catchError(() => of([])),
    )
  }
}
